import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, RotateCcw, CheckCircle, Loader2, User } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Encomenda, EncomendaStatus } from '../../../services/encomendaApi';
import { fetchWithAuth, getApiBaseUrl } from '../../../services/api';

interface Resident {
  id: string;
  first_name: string;
  last_name: string;
  roles?: { role_type: string; role_metadata?: { relation_type?: string } }[];
}

interface EncomendaCheckoutModalProps {
  open: boolean;
  encomenda: Encomenda | null;
  action: 'picked_up' | 'returned';
  customerId?: string;
  onConfirm: (status: EncomendaStatus, opts: { signature_data?: string; notes?: string }) => Promise<void>;
  onClose: () => void;
}

function useSignaturePad(canvasRef: React.RefObject<HTMLCanvasElement>, open: boolean) {
  const isDrawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const src = 'touches' in e ? e.touches[0] : e;
      return {
        x: (src.clientX - rect.left) * scaleX,
        y: (src.clientY - rect.top) * scaleY,
      };
    };

    const onStart = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDrawing.current = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };
    const onMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!isDrawing.current) return;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      setHasDrawn(true);
    };
    const onEnd = () => { isDrawing.current = false; };

    canvas.addEventListener('mousedown', onStart);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onEnd);
    canvas.addEventListener('mouseleave', onEnd);
    canvas.addEventListener('touchstart', onStart, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    canvas.addEventListener('touchend', onEnd);

    return () => {
      canvas.removeEventListener('mousedown', onStart);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup', onEnd);
      canvas.removeEventListener('mouseleave', onEnd);
      canvas.removeEventListener('touchstart', onStart);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onEnd);
    };
  }, [canvasRef, open]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, [canvasRef]);

  const getDataUrl = useCallback(() => canvasRef.current?.toDataURL('image/png'), [canvasRef]);

  return { hasDrawn, clear, getDataUrl };
}

export default function EncomendaCheckoutModal({
  open,
  encomenda,
  action,
  customerId,
  onConfirm,
  onClose,
}: EncomendaCheckoutModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { hasDrawn, clear, getDataUrl } = useSignaturePad(canvasRef, open);
  const [submitting, setSubmitting] = useState(false);
  const [confirmNoSig, setConfirmNoSig] = useState(false);

  // Resident selection
  const [residents, setResidents] = useState<Resident[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<string>('');
  const [otherName, setOtherName] = useState('');
  const [loadingResidents, setLoadingResidents] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmNoSig(false);
      setSubmitting(false);
      setSelectedResidentId('');
      setOtherName('');
      setResidents([]);
      clear();
    }
  }, [open, clear]);

  // Fetch residents for the unit
  useEffect(() => {
    if (!open || !encomenda?.unit_id || !customerId) return;
    setLoadingResidents(true);
    fetchWithAuth(
      `${getApiBaseUrl()}/api/v1/customers/${customerId}/people?role_type=resident&unit_id=${encomenda.unit_id}&page_size=50`
    )
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setResidents(data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingResidents(false));
  }, [open, encomenda?.unit_id, customerId]);

  if (!open || !encomenda) return null;

  const isPickup = action === 'picked_up';

  const RELATION_LABELS: Record<string, string> = {
    proprietario: 'Proprietário',
    inquilino: 'Inquilino',
    dependente: 'Dependente',
    familiar: 'Familiar',
    conjuge: 'Cônjuge',
  };

  const getRelationType = (r: Resident): string | null => {
    const rel = (r as any).relation_type ||
      r.roles?.find(ro => ro.role_type === 'resident')?.role_metadata?.relation_type;
    return rel ? (RELATION_LABELS[rel] ?? rel) : null;
  };
  const actionLabel = isPickup ? 'Retirada' : 'Devolução';

  const unitLabel = encomenda.unit
    ? encomenda.unit.block?.name
      ? `${encomenda.unit.block.name} / Ap ${encomenda.unit.number}`
      : `Ap ${encomenda.unit.number}`
    : '—';
  const personLabel = encomenda.person
    ? `${encomenda.person.first_name} ${encomenda.person.last_name}`
    : null;

  const getReceiverName = (): string | null => {
    if (!selectedResidentId) return null;
    if (selectedResidentId === 'other') return otherName.trim() || null;
    const r = residents.find(r => r.id === selectedResidentId);
    return r ? `${r.first_name} ${r.last_name}` : null;
  };

  const buildNotes = (base?: string): string | undefined => {
    const name = getReceiverName();
    const parts: string[] = [];
    if (name) parts.push(`Recebido por: ${name}`);
    if (base) parts.push(base);
    return parts.length ? parts.join(' — ') : undefined;
  };

  const handleConfirmWithSignature = async () => {
    const dataUrl = getDataUrl();
    if (!dataUrl || !hasDrawn) return;
    setSubmitting(true);
    try {
      await onConfirm(action, { signature_data: dataUrl, notes: buildNotes() });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmNoSignature = async () => {
    setSubmitting(true);
    try {
      await onConfirm(action, { notes: buildNotes('Registrado sem assinatura') });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Confirmar {actionLabel}</p>
            <p className="text-sm font-bold text-gray-900">{unitLabel}</p>
            {personLabel && <p className="text-xs text-gray-500">{personLabel}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Resident selection */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              Quem está {isPickup ? 'retirando' : 'devolvendo'}?
            </p>
            {loadingResidents ? (
              <div className="flex items-center gap-2 text-gray-400 text-xs py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Carregando moradores...
              </div>
            ) : (
              <div className="space-y-1.5">
                {residents.map(r => (
                  <label
                    key={r.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                      selectedResidentId === r.id
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <input
                      type="radio"
                      name="resident"
                      value={r.id}
                      checked={selectedResidentId === r.id}
                      onChange={() => setSelectedResidentId(r.id)}
                      className="accent-emerald-500"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-800 font-medium">
                        {r.first_name} {r.last_name}
                      </span>
                      {getRelationType(r) && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
                          {getRelationType(r)}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
                {/* Other person option */}
                <label
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                    selectedResidentId === 'other'
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  )}
                >
                  <input
                    type="radio"
                    name="resident"
                    value="other"
                    checked={selectedResidentId === 'other'}
                    onChange={() => setSelectedResidentId('other')}
                    className="accent-amber-500"
                  />
                  <span className="text-sm text-gray-600">Outra pessoa</span>
                </label>
                {selectedResidentId === 'other' && (
                  <input
                    type="text"
                    value={otherName}
                    onChange={e => setOtherName(e.target.value)}
                    placeholder="Nome de quem está retirando..."
                    autoFocus
                    className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-amber-400"
                  />
                )}
              </div>
            )}
          </div>

          {/* Signature area */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Assinatura do destinatário
            </p>
            <div className="relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden">
              <canvas
                ref={canvasRef}
                width={460}
                height={160}
                className="w-full touch-none cursor-crosshair"
                style={{ display: 'block' }}
              />
              {!hasDrawn && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-300 text-sm select-none">Assine aqui</p>
                </div>
              )}
            </div>
            {hasDrawn && (
              <button
                onClick={clear}
                className="mt-1.5 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Limpar
              </button>
            )}
          </div>

          {/* Confirm with signature */}
          <button
            onClick={handleConfirmWithSignature}
            disabled={!hasDrawn || submitting}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all',
              hasDrawn && !submitting
                ? isPickup
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-gray-700 hover:bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            Confirmar {actionLabel} com assinatura
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* No signature path */}
          {!confirmNoSig ? (
            <button
              onClick={() => setConfirmNoSig(true)}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition-colors"
            >
              <AlertTriangle className="h-4 w-4" />
              Registrar sem assinatura
            </button>
          ) : (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2.5">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>Atenção:</strong> Esta ação será registrada sem assinatura. Sua identidade será salva como responsável por esta confirmação.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmNoSig(false)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmNoSignature}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white transition-colors flex items-center justify-center gap-1"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirmar mesmo assim'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
